import { bizGet, bizPatch, bizPost } from '@/shared/api/bizClient';
import type { CashierOrder } from '@/features/cashier/types';

import type {
  BoardingCustomerReport,
  BoardingStayDetail,
  BoardingStayInput,
  BoardingStaySummary,
  BoardingStayUpdateInput,
  BoardingStayUpdateEventInput,
  BoardingUnitInput,
  BoardingUnitStatus,
  BoardingUnitSummary,
} from './types';

interface ListEnvelope<T> {
  items: T[];
}

export async function listBoardingUnits(branchId: string): Promise<BoardingUnitSummary[]> {
  const response = await bizGet<ListEnvelope<BoardingUnitSummary>>(
    '/biz/boarding/units',
    { params: { branchId } },
  );
  return response.items;
}

export async function listBoardingStays(branchId: string): Promise<BoardingStaySummary[]> {
  const response = await bizGet<ListEnvelope<BoardingStaySummary>>(
    '/biz/boarding/stays',
    { params: { branchId } },
  );
  return response.items;
}

export async function upsertBoardingUnit(input: BoardingUnitInput): Promise<BoardingUnitSummary> {
  return bizPost<BoardingUnitSummary, BoardingUnitInput>(
    '/biz/boarding/units',
    input,
  );
}

export async function updateBoardingUnitStatus(
  unitId: string,
  status: BoardingUnitStatus,
): Promise<BoardingUnitSummary> {
  return bizPatch<BoardingUnitSummary, { status: BoardingUnitStatus }>(
    `/biz/boarding/units/${unitId}/status`,
    { status },
  );
}

export async function ensureBoardingStay(
  bookingId: string,
  input: BoardingStayInput,
): Promise<BoardingStayDetail> {
  return bizPost<BoardingStayDetail, BoardingStayInput>(
    `/biz/boarding/bookings/${bookingId}/stay`,
    input,
  );
}

export async function updateBoardingStay(
  stayId: string,
  input: BoardingStayUpdateInput,
): Promise<BoardingStayDetail> {
  return bizPatch<BoardingStayDetail, BoardingStayUpdateInput>(
    `/biz/boarding/stays/${stayId}`,
    input,
  );
}

export async function addBoardingStayUpdate(
  stayId: string,
  input: BoardingStayUpdateEventInput,
): Promise<BoardingStayDetail> {
  return bizPost<BoardingStayDetail, BoardingStayUpdateEventInput>(
    `/biz/boarding/stays/${stayId}/update`,
    input,
  );
}

export async function getBoardingCustomerReport(stayId: string): Promise<BoardingCustomerReport> {
  return bizGet<BoardingCustomerReport>(
    `/biz/boarding/stays/${stayId}/customer-report`,
  );
}

export async function bindBoardingStayQuote(stayId: string): Promise<BoardingStayDetail> {
  return bizPost<BoardingStayDetail, Record<string, never>>(
    `/biz/boarding/stays/${stayId}/quote-binding`,
    {},
  );
}

export async function createBoardingCashierOrder(stayId: string): Promise<CashierOrder> {
  return bizPost<CashierOrder, Record<string, never>>(
    `/biz/boarding/stays/${stayId}/cashier-order`,
    {},
  );
}
